import { components, operations } from '@/schema'

// Message transaction types
export type MessageConditionSingleResponse =
	components['schemas']['MessageConditionSingleResponse']
export type MessageConditionAutoResponse =
	components['schemas']['MessageConditionAutoResponse']
export type GetPlayerByConditionRequest =
	components['schemas']['GetPlayerByConditionDto']
export type CreateMessageTransactionRequest =
	components['schemas']['CreateMessageTransactionDto']
export type GetMessageResponse = components['schemas']['GetMessageResponse']
export type GetMessageTransactionHistoryRequest =
	components['schemas']['GetMessageTransactionHistoryDto']
export type GetMessageTransactionHistoryResponse =
	components['schemas']['GetMessageTransactionHistoryResponse']
export type MessageTransactionModel =
	components['schemas']['MessageTransactionModel']

// Auto message types
export type GetAutoMessageResponse =
	components['schemas']['GetAutoMessageResponse']
export type UpdateAutoMessageRequest =
	components['schemas']['UpdateAutoMessageDto']
export type UpdateAutoMessageResponse =
	components['schemas']['UpdateAutoMessageDto']

// Interval message types
export type IntervalMessageTransactionModel =
	components['schemas']['IntervalMessageTransactionModel']
export type GetIntervalMessagesResponse =
	components['schemas']['GetIntervalMessagesResponse']
export type CreateIntervalMessageDto =
	components['schemas']['CreateIntervalMessageDto']
export type UpdateIntervalMessageDto =
	components['schemas']['UpdateIntervalMessageDto']
export type IntervalSmsTransactionModel =
	components['schemas']['IntervalSmsTransactionModel']
export type IntervalLineTransactionModel =
	components['schemas']['IntervalLineTransactionModel']
export type IntervalMessageInfo = components['schemas']['IntervalMessageInfo']

// Channel transaction types
export type GetChannelTransactionsRequest =
	components['schemas']['GetChannelTransactionsDto']
export type GetChannelTransactionsResponse =
	components['schemas']['GetChannelTransactionsResponse']
export type SmsTransactionWithCampaign =
	components['schemas']['SmsTransactionWithCampaign']
export type LineTransactionWithCampaign =
	components['schemas']['LineTransactionWithCampaign']

// SMS provider types
export type GetSmsProvidersResponse =
	components['schemas']['GetSmsProvidersResponse'][]
export type GetSmsProviderSendersResponse =
	components['schemas']['GetSmsProviderSendersResponse'][]

// Condition config types
export type MessageConditionSingleConfigDto =
	components['schemas']['MessageConditionSingleConfigDto']
export type MessageConditionAutoConfigDto =
	components['schemas']['MessageConditionAutoConfigDto']

// Player types
export type PlayerForMessagingModel =
	components['schemas']['PlayerForMessagingModel']

// Export enums from schema
export {
	MessageChannel,
	MessageTransactionType,
	MessageGroupCondition,
	MessageConditionType,
	MessageConditionSingle,
	MessageConditionAuto,
	MessageCondition, // New combined enum
	AutoMessageGroupCondition,
	SmsTransactionStatus,
	IntervalCondition,
	LineTransactionType,
} from '@/schema'

// LINE error types (temporary until schema is updated)
export enum LineErrorType {
	GENERAL = 'GENERAL',
	EXCEED_QUOTA_LIMIT = 'EXCEED_QUOTA_LIMIT',
}

// Form value types
export interface MessageFormValues {
	sendType: components['schemas']['MessageTransactionType']
	channels: components['schemas']['MessageChannel'][]
	message: string
	scheduledAt?: Date
	// SMS specific
	providerId?: string
	senderId?: string
	// Hot games
	hotGameFlexMessageId?: string
	// Promotion
	promotionId?: string
}

// Messaging analytics types
export type MessagingAnalyticsResponse =
	components['schemas']['MessagingAnalyticsResponse']
export type ChannelAnalytics = components['schemas']['ChannelAnalytics']
export type CampaignAnalytics = components['schemas']['CampaignAnalytics']
export type ConversionRateByDays = components['schemas']['ConversionRateByDays']
export type ConversionMetricsByDays =
	components['schemas']['ConversionMetricsByDays']
export type CategoryConversionMetrics =
	components['schemas']['CategoryConversionMetrics']

// Analytics query params
export type GetMessagingAnalyticsParams =
	operations['MessageController_getMessagingAnalytics']['parameters']['query']

// Flex message analytics types
export type FlexMessageAnalyticsOverall =
	components['schemas']['FlexMessageAnalyticsOverall']
export type FlexMessageAnalyticsResponse =
	components['schemas']['FlexMessageAnalyticsResponse']
export type FlexMessageAnalyticsListResponse =
	components['schemas']['FlexMessageAnalyticsListResponse']
export type OpensByDayWindowResponse =
	components['schemas']['OpensByDayWindowResponse']

// Broke Flex Message types (LINE message campaigns)
export type BrokeFlexMessageCampaignModel =
	components['schemas']['AdminBrokeFlexMessageCampaignModel']
export type BrokeFlexMessageCampaignResponse =
	components['schemas']['AdminBrokeFlexMessageCampaignResponse']
export type BrokeFlexMessageCampaignListResponse =
	components['schemas']['AdminBrokeFlexMessageCampaignListResponse']
export type BrokeFlexMessageStageModel =
	components['schemas']['AdminBrokeFlexMessageStageModel']
export type BrokeFlexMessageItemModel =
	components['schemas']['AdminBrokeFlexMessageItemModel']
// Commented out until schema is regenerated
// export type BrokeFlexMessageStageResponse =
// 	components['schemas']['AdminBrokeFlexMessageStageResponse']
// export type BrokeFlexMessageItemResponse =
// 	components['schemas']['AdminBrokeFlexMessageItemResponse']
export type CreateBrokeFlexMessageCampaignRequest =
	components['schemas']['CreateBrokeFlexMessageCampaignDto']
export type UpdateBrokeFlexMessageCampaignRequest =
	components['schemas']['UpdateBrokeFlexMessageCampaignDto']
export type CreateBrokeFlexMessageStageDto =
	components['schemas']['CreateBrokeFlexMessageStageDto']
export type CreateBrokeFlexMessageItemDto =
	components['schemas']['CreateBrokeFlexMessageItemDto']

// Export enums from schema
export {
	CreateBrokeFlexMessageItemDtoType,
	CreateBrokeFlexMessageItemDtoEligibilityType,
} from '@/schema'

// Broke Flex Message Analytics types (temporary until schema is regenerated)
export type BrokeFlexAnalyticsResponse = {
	id: string
	autoMessageTransactionId: string
	campaignName?: string
	totalRecipients: number
	uniqueOpeners: number
	totalOpens: number
	uniquePlayers: number
	totalPlays: number
	openRate?: number
	playRate?: number
	avgHoursToOpen?: number
	avgHoursToPlay?: number
	opensByDayWindow: Record<string, number>
	walletTypeBreakdown: Record<string, any>
	balanceRangeBreakdown: Record<string, any>
	gameBreakdown: Record<string, any>
	depositConversions: number
	totalDepositAmount: number
	lastCalculatedAt: string | Date
	createdAt: string | Date
	updatedAt: string | Date
}

export type BrokeFlexAnalyticsListResponse = {
	success: boolean
	data: BrokeFlexAnalyticsResponse[]
	overall?: {
		totalRecipients: number
		uniqueOpeners: number
		totalOpens: number
		uniquePlayers: number
		totalPlays: number
		openRate: number
		playRate: number
		avgHoursToOpen: number
		avgHoursToPlay: number
		depositConversions: number
		totalDepositAmount: number
		opensByDayWindow: Record<string, number>
	}
}

// Promotion Flex Message Analytics types (temporary until schema is regenerated)
export type PromotionFlexAnalyticsResponse = {
	id: string
	messageTransactionId?: string
	autoMessageTransactionId?: string
	promotionIds: string[]
	promotionNames?: string[]
	totalRecipients: number
	uniqueClicks: number
	totalClicks: number
	uniqueClaimers: number
	totalClaims: number
	depositConversions: number
	totalDepositAmount: number
	clickRate?: number
	claimRate?: number
	avgHoursToClick?: number
	avgHoursToClaim?: number
	promotionBreakdown: Record<string, any>
	lastCalculatedAt: string | Date
	createdAt: string | Date
	updatedAt: string | Date
}
